import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import { clearSessionKeys } from "../utils/sessionKeys";
import { clearSessionAcknowledged } from "../utils/sessionGate";
import {
  resolveInactivityMs,
  SESSION_INACTIVITY_CHECK_MS,
} from "../utils/sessionInactivityConfig";
import { createSessionInactivityTimer } from "../utils/sessionInactivityTimer";

/**
 * Logs out after inactivity when enabled for authenticated areas.
 */
export function useSessionInactivity(enabled) {
  const navigate = useNavigate();
  const inactivityMs = resolveInactivityMs();

  useEffect(() => {
    if (!enabled) return undefined;

    let loggingOut = false;

    const logoutForInactivity = async () => {
      if (loggingOut) return;
      loggingOut = true;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      try {
        await fetch(`${API_URL}/logout`, {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
        });
      } catch {
        /* proceed with client cleanup */
      } finally {
        clearTimeout(timeoutId);
        clearSessionKeys();
        clearSessionAcknowledged();
        navigate("/login?reason=inactivity", { replace: true });
      }
    };

    const timer = createSessionInactivityTimer({
      inactivityMs,
      checkIntervalMs: SESSION_INACTIVITY_CHECK_MS,
      onTimeout: () => {
        void logoutForInactivity();
      },
    });

    return timer.start();
  }, [enabled, inactivityMs, navigate]);
}
